import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, CheckCircle, Clock, AlertTriangle,
  Filter, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { AdminLayout } from './AdminDashboard';
import { actionAPI, surveyAPI, employeeAPI } from '../lib/api';
import { format } from 'date-fns';

const ActionTracker = () => {
  const [actions, setActions] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [filters, setFilters] = useState({ survey_id: '', department: '', status: '' });
  
  const [formData, setFormData] = useState({
    survey_id: '',
    department: '',
    issue: '',
    action: '',
    assigned_to: '',
    due_date: new Date(),
    priority: 'medium'
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadActions();
  }, [filters]);

  const loadData = async () => {
    try {
      const [surveyRes, deptRes] = await Promise.all([
        surveyAPI.getAll(),
        employeeAPI.getDepartments()
      ]);
      setSurveys(surveyRes.data);
      setDepartments(deptRes.data.departments || []);
    } catch (error) {
      console.error('Failed to load data');
    }
  };

  const loadActions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.survey_id) params.survey_id = filters.survey_id;
      if (filters.department) params.department = filters.department;
      if (filters.status) params.status = filters.status;
      
      const response = await actionAPI.getAll(params);
      setActions(response.data);
    } catch (error) {
      toast.error('Failed to load action items');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      survey_id: '',
      department: '',
      issue: '',
      action: '',
      assigned_to: '',
      due_date: new Date(),
      priority: 'medium'
    });
    setEditingAction(null);
  };

  const handleOpenDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleEdit = (action) => {
    setFormData({
      survey_id: action.survey_id,
      department: action.department,
      issue: action.issue,
      action: action.action,
      assigned_to: action.assigned_to,
      due_date: new Date(action.due_date),
      priority: action.priority
    });
    setEditingAction(action);
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.survey_id || !formData.department || !formData.issue || !formData.action) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingAction) {
        await actionAPI.update(editingAction.id, {
          ...formData,
          due_date: formData.due_date.toISOString()
        });
        toast.success('Action item updated');
      } else {
        await actionAPI.create({
          ...formData,
          due_date: formData.due_date.toISOString()
        });
        toast.success('Action item created');
      }
      setShowDialog(false);
      resetForm();
      loadActions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save action item');
    }
  };

  const handleStatusChange = async (action, newStatus) => {
    try {
      await actionAPI.update(action.id, { status: newStatus });
      toast.success('Status updated');
      loadActions();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (actionId) => {
    if (!window.confirm('Delete this action item?')) return;
    
    try {
      await actionAPI.delete(actionId);
      toast.success('Action item deleted');
      loadActions();
    } catch (error) {
      toast.error('Failed to delete action item');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-accent"><CheckCircle className="h-3 w-3 mr-1" /> Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-chart-4 text-primary"><Clock className="h-3 w-3 mr-1" /> In Progress</Badge>;
      default:
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return <Badge variant="destructive">High</Badge>;
      case 'medium':
        return <Badge className="bg-secondary">Medium</Badge>;
      default:
        return <Badge variant="outline">Low</Badge>;
    }
  };

  const stats = {
    total: actions.length,
    pending: actions.filter(a => a.status === 'pending').length,
    inProgress: actions.filter(a => a.status === 'in_progress').length,
    completed: actions.filter(a => a.status === 'completed').length
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2">Action Tracker</h1>
            <p className="text-muted-foreground">Track improvement actions from survey insights</p>
          </div>
          <Button onClick={handleOpenDialog} data-testid="add-action-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Action Item
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="font-heading text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Pending</p>
              <p className="font-heading text-2xl font-bold text-muted-foreground">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">In Progress</p>
              <p className="font-heading text-2xl font-bold text-chart-4">{stats.inProgress}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="font-heading text-2xl font-bold text-accent">{stats.completed}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <Select 
                value={filters.survey_id || '__all__'} 
                onValueChange={(value) => setFilters({ ...filters, survey_id: value === '__all__' ? '' : value })}
              >
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="All Surveys" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Surveys</SelectItem>
                  {surveys.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={filters.department || '__all__'} 
                onValueChange={(value) => setFilters({ ...filters, department: value === '__all__' ? '' : value })}
              >
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Departments</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={filters.status || '__all__'} 
                onValueChange={(value) => setFilters({ ...filters, status: value === '__all__' ? '' : value })}
              >
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Action Items List */}
        <Card data-testid="actions-list">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : actions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No action items found
              </div>
            ) : (
              <div className="divide-y divide-border">
                {actions.map((action) => (
                  <div 
                    key={action.id}
                    className="p-4 hover:bg-muted/50 transition-colors"
                    data-testid={`action-item-${action.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(action.status)}
                          {getPriorityBadge(action.priority)}
                          <Badge variant="outline">{action.department}</Badge>
                        </div>
                        <h3 className="font-medium mb-1">{action.issue}</h3>
                        <p className="text-sm text-muted-foreground mb-2">{action.action}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Assigned: {action.assigned_to}</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Due: {format(new Date(action.due_date), 'MMM dd, yyyy')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={action.status}
                          onValueChange={(value) => handleStatusChange(action, value)}
                        >
                          <SelectTrigger className="w-32 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(action)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDelete(action.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingAction ? 'Edit Action Item' : 'Add Action Item'}
              </DialogTitle>
              <DialogDescription>
                Create an action item to track improvement initiatives
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Survey</Label>
                  <Select 
                    value={formData.survey_id}
                    onValueChange={(value) => setFormData({ ...formData, survey_id: value })}
                  >
                    <SelectTrigger data-testid="action-survey-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {surveys.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select 
                    value={formData.department}
                    onValueChange={(value) => setFormData({ ...formData, department: value })}
                  >
                    <SelectTrigger data-testid="action-department-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Issue Identified</Label>
                <Textarea
                  value={formData.issue}
                  onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
                  placeholder="Describe the issue identified from survey..."
                  data-testid="action-issue-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Proposed Action</Label>
                <Textarea
                  value={formData.action}
                  onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                  placeholder="What action will be taken..."
                  data-testid="action-action-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Assigned To</Label>
                  <Input
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    placeholder="Person responsible"
                    data-testid="action-assigned-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <Calendar className="h-4 w-4 mr-2" />
                      {format(formData.due_date, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={formData.due_date}
                      onSelect={(date) => date && setFormData({ ...formData, due_date: date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-action-btn">
                  {editingAction ? 'Update' : 'Create'} Action
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ActionTracker;
